import React, { useRef, useEffect } from 'react';
import { AuditTimelineEvent } from '../../../interfaces/Audit';
import './ChecklistEditDetailsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import { ehFamiliaSaida } from '../../../shared/checklist-familias';

interface ChecklistEditDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: AuditTimelineEvent | null;
}

const ITEM_STATUS_LABELS: Record<string, string> = {
  OK: 'OK',
  DAMAGED: 'Avariado',
  MISSING: 'Ausente',
  NA: 'N/A',
};

const itemStatusLabel = (status?: string | null) =>
  (status && ITEM_STATUS_LABELS[status]) || status || '-';

const MEDIA_TYPE_LABELS: Record<string, string> = {
  PHOTO: 'Foto',
  VIDEO: 'Vídeo',
};

const mediaTypeLabel = (type?: string | null) =>
  (type && MEDIA_TYPE_LABELS[type]) || 'Mídia';

/** Nome da seção onde a mídia adicionada foi colocada. */
const addedMediaSection = (media: Record<string, any>) => {
  if (media.sectionName) return media.sectionName;
  if (media.category === 'ANGLES') return `Ângulo ${media.slotKey || ''}`.trim();
  if (media.category === 'SERVICE') return 'Imagens de saída';
  return media.slotKey || 'Checklist';
};

/** Nome da seção de onde a mídia excluída saiu. */
const removedMediaSection = (media: Record<string, any>) => {
  if (media.slotLabel) return media.slotLabel;
  if (media.folder === 'SERVICO') return 'Imagens de saída';
  if (media.slotKey) return `Ângulo ${media.slotKey}`;
  return 'Checklist';
};

/** Miniatura da mídia (clique abre em nova aba); vídeos aparecem como player compacto. */
const MediaPreview: React.FC<{ media: Record<string, any> }> = ({ media }) => {
  if (!media.previewUrl) {
    return <div className="checklist-edit-preview-unavailable">Preview indisponível</div>;
  }

  if (media.mediaType === 'VIDEO') {
    return (
      <video className="checklist-edit-preview-video" src={media.previewUrl} controls preload="metadata" />
    );
  }

  return (
    <a href={media.previewUrl} target="_blank" rel="noopener noreferrer" title="Abrir em tamanho real">
      <img className="checklist-edit-preview-img" src={media.previewUrl} alt="Preview da mídia" loading="lazy" />
    </a>
  );
};

const ChecklistEditDetailsModal: React.FC<ChecklistEditDetailsModalProps> = ({
  isOpen,
  onClose,
  event,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const metadata = event.metadata || {};
  const itemChanges: Array<Record<string, any>> = metadata.itemChanges || [];
  const mediaAdded: Array<Record<string, any>> = metadata.mediaAdded || [];
  const mediaRemoved: Array<Record<string, any>> = metadata.mediaRemoved || [];
  // at-50: mudança das observações gerais da sessão (ausente em registros antigos)
  const observationChange: Record<string, any> | null = metadata.observationChange || null;
  const observationLabel = ehFamiliaSaida(metadata.checklistType)
    ? 'Observações de Saída'
    : 'Observações de Entrada';

  const performer = event.collaboratorNames.length > 0
    ? event.collaboratorNames.join(', ')
    : 'Sistema';

  const dateStr = new Date(event.occurredAt).toLocaleDateString('pt-BR');
  const timeStr = new Date(event.occurredAt).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const isEmpty =
    itemChanges.length === 0 && mediaAdded.length === 0 && mediaRemoved.length === 0 && !observationChange;

  return (
    <div className="checklist-edit-modal-overlay">
      <div className="checklist-edit-modal-content" ref={modalRef}>
        <div className="checklist-edit-modal-header">
          <h2>{event.title}</h2>
          <button className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="checklist-edit-modal-metadata">
          <div className="metadata-item">
            <strong>Editado por:</strong> <span>{performer}</span>
          </div>
          <div className="metadata-item">
            <strong>Data da edição:</strong> <span>{dateStr} às {timeStr}</span>
          </div>
        </div>

        <div className="checklist-edit-modal-body">
          {isEmpty && (
            <div className="checklist-edit-empty">
              Este registro não contém o detalhamento das alterações.
            </div>
          )}

          {itemChanges.length > 0 && (
            <div className="checklist-edit-section">
              <div className="checklist-edit-section-header">
                Itens alterados ({itemChanges.length})
              </div>
              <div className="checklist-edit-list">
                {itemChanges.map((change, index) => (
                  <div key={`item-${change.templateKey || index}`} className="checklist-edit-row">
                    <div className="checklist-edit-row-title">{change.label || change.templateKey}</div>
                    <div className="checklist-edit-diff">
                      <span className="diff-before">{itemStatusLabel(change.before?.status)}</span>
                      <span className="diff-arrow">→</span>
                      <span className="diff-after">{itemStatusLabel(change.after?.status)}</span>
                    </div>
                    {((change.before?.note || '') !== (change.after?.note || '')) && (
                      <div className="checklist-edit-note">
                        <div><strong>Observação anterior:</strong> {change.before?.note || '—'}</div>
                        <div><strong>Observação nova:</strong> {change.after?.note || '—'}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {observationChange && (
            <div className="checklist-edit-section">
              <div className="checklist-edit-section-header">{observationLabel}</div>
              <div className="checklist-edit-list">
                <div className="checklist-edit-row">
                  <div className="checklist-edit-note">
                    <div><strong>Texto anterior:</strong> {observationChange.before || '—'}</div>
                    <div><strong>Texto novo:</strong> {observationChange.after || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {mediaAdded.length > 0 && (
            <div className="checklist-edit-section">
              <div className="checklist-edit-section-header">
                Mídias adicionadas ({mediaAdded.length})
              </div>
              <div className="checklist-edit-list">
                {mediaAdded.map((media, index) => (
                  <div key={`added-${media.mediaId || index}`} className="checklist-edit-row row-added row-with-preview">
                    <MediaPreview media={media} />
                    <div className="checklist-edit-row-info">
                      <div className="checklist-edit-row-title">
                        {mediaTypeLabel(media.mediaType)}{media.filename ? ` — ${media.filename}` : ''}
                      </div>
                      <div className="checklist-edit-row-subtitle">Seção: {addedMediaSection(media)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mediaRemoved.length > 0 && (
            <div className="checklist-edit-section">
              <div className="checklist-edit-section-header">
                Mídias excluídas ({mediaRemoved.length})
              </div>
              <div className="checklist-edit-list">
                {mediaRemoved.map((media, index) => (
                  <div key={`removed-${media.mediaId || index}`} className="checklist-edit-row row-removed row-with-preview">
                    <MediaPreview media={media} />
                    <div className="checklist-edit-row-info">
                      <div className="checklist-edit-row-title">{mediaTypeLabel(media.mediaType)}</div>
                      <div className="checklist-edit-row-subtitle">Seção: {removedMediaSection(media)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistEditDetailsModal;
